import angular from 'angular'

import { last, random } from 'lodash-es'

import postFlag from '../utils/post-flag'
import StrictObj from '../utils/strict-obj'
import MapSimple from '../utils/map-simple'
import jsFullscreen from '../utils/js-fullscreen'

import {
  keydownH,
  keydownF,
  keydownShiftH,
  keydownShiftF
} from '../utils/keydown'

angular
  .module('ggs.gallery')
  .factory('PresentationManager', PresentationManagerProvider)

const EDGE_OFFSET = 80

function PresentationManagerProvider(
  qh,
  $log,
  $timeout,
  modalLayout,
  DefineMethod,
  DefineHandler) {

  return function PresentationManager(
    properties,
    extOne,
    extList,
    extCampaign,
    postListManager,
    openSamplePost) {

    const sign = 'galleryMain::presentationManager'
    const postList = postListManager.getPostList()
    const curr2prev = MapSimple().setMaxPairs(1)
    const curr2next = MapSimple().setMaxPairs(1)

    let timeoutId = null

    const manager = StrictObj({
      wrapM: DefineMethod(sign),
      wrapH: DefineHandler(sign)
    })

    manager.wrapM('init', () => {
      keydownH.on(manager.hKeyHandler)
      keydownF.on(manager.fKeyHandler)
      keydownShiftH.on(manager.shiftHKeyHandler)
      keydownShiftF.on(manager.shiftFKeyHandler)
      jsFullscreen.rootExitEmitter.on(manager.exitHandler)
      properties.key2emitter.presentation_auto_control.on(manager.performTickRequestHandler)
      properties.key2emitter.presentation_tick_interval.on(manager.performTickRequestHandler)
      return manager.performTick()
    })

    manager.wrapM('destroy', () => {
      keydownH.off(manager.hKeyHandler)
      keydownF.off(manager.fKeyHandler)
      keydownShiftH.off(manager.shiftHKeyHandler)
      keydownShiftF.off(manager.shiftFKeyHandler)
      jsFullscreen.rootExitEmitter.off(manager.exitHandler)
      properties.key2emitter.presentation_auto_control.off(manager.performTickRequestHandler)
      properties.key2emitter.presentation_tick_interval.off(manager.performTickRequestHandler)
      return manager.cancelTimeout()
    })

    manager.wrapH('hKeyHandler', () => {
      if (!properties.allow_presentation_mode) {
        return $log.warn(`${sign}#hKeyHandler: presentation mode is not allowed!`)
      }

      jsFullscreen.request()
      properties.override({ allow_header: false })
      properties.override({
        presentation_mode: true,
        presentation_control_type: 'auto'
      })
    })

    manager.wrapH('fKeyHandler', () => {
      if (!properties.allow_presentation_mode) {
        return $log.warn(`${sign}#fKeyHandler: presentation mode is not allowed!`)
      }

      jsFullscreen.request()
      properties.unoverride('allow_header')
      properties.override({
        presentation_mode: true,
        presentation_control_type: 'auto'
      })
    })

    manager.wrapH('shiftHKeyHandler', () => {
      if (!properties.allow_presentation_mode) {
        return $log.warn(`${sign}#shiftHKeyHandler: presentation mode is not allowed!`)
      }

      jsFullscreen.request()
      properties.override({ allow_header: false })
      properties.override({
        presentation_mode: true,
        presentation_control_type: 'manual'
      })
    })

    manager.wrapH('shiftFKeyHandler', () => {
      if (!properties.allow_presentation_mode) {
        return $log.warn(`${sign}#shiftFKeyHandler: presentation mode is not allowed!`)
      }

      jsFullscreen.request()
      properties.unoverride('allow_header')
      properties.override({
        presentation_mode: true,
        presentation_control_type: 'manual'
      })
    })

    manager.wrapH('exitHandler', () => {
      properties.unoverride(
        'allow_header',
        'presentation_mode',
        'presentation_control_type')
    })

    manager.wrapM('isListClosed', qh.makeAnd(properties.key2get.modals_use, extList.isClosed))

    manager.wrapM('isOneOpen', qh.makeAnd(properties.key2get.modals_use, extOne.isOpen))
    manager.wrapM('isListOpen', qh.makeAnd(properties.key2get.modals_use, extList.isOpen))
    manager.wrapM('isCampaignOpen', qh.makeAnd(properties.key2get.allow_entry, extCampaign.isOpen))

    manager.wrapM('closeOne', qh.makeIf(manager.isOneOpen, extOne.close))
    manager.wrapM('closeCampaign', qh.makeIf(manager.isCampaignOpen, extCampaign.close))

    manager.wrapM('openList', qh.makeIf(properties.key2get.modals_use, openSamplePost))
    manager.wrapM('prevList', qh.makeIf(manager.isListOpen, extList.prev))
    manager.wrapM('nextList', qh.makeIf(manager.isListOpen, extList.next))

    manager.wrapM('direction', () => {
      if (curr2prev.size() === 0) return false
      if (curr2next.size() === 0) return true
      let prevCoef = -1
      let nextCoef = 0
      if (!postListManager.isUnprocessedAboveListEmpty()) prevCoef -= 2
      if (!postListManager.isUnprocessedBelowListEmpty()) nextCoef += 1
      return random(prevCoef, nextCoef) < 0
    })

    manager.wrapM('setEdgeOffset', () => {
      modalLayout.setExtraWidthOffset(EDGE_OFFSET)
      modalLayout.setExtraHeightOffset(EDGE_OFFSET)
      modalLayout.resizeEmitter.emit()
    })

    manager.wrapM('clearEdgeOffset', () => {
      modalLayout.setExtraWidthOffset(0)
      modalLayout.setExtraHeightOffset(0)
      modalLayout.resizeEmitter.emit()
    })

    manager.wrapM('setupTimeout', () => {
      const solidInterval = properties.presentation_tick_interval
      const deviation = Math.round(properties.presentation_tick_interval / 10)
      const interval = random(solidInterval - deviation, solidInterval + deviation)
      timeoutId = $timeout(manager.performTickRequestHandler, interval)
    })

    manager.wrapM('cancelTimeout', () => {
      $timeout.cancel(timeoutId)
      timeoutId = null
    })

    manager.wrapM('manageExternal',
      qh.makeOver(
        qh.makeJoin(
          manager.closeOne,
          manager.closeCampaign),
        qh.makeIfElse(
          manager.isListClosed,
          manager.openList,
          qh.makeIfElse(
            manager.direction,
            manager.prevList,
            manager.nextList))))

    manager.wrapM('performTick',
      qh.makeIfElse(
        properties.key2get.presentation_auto_control,
        qh.makeOver(
          manager.setEdgeOffset,
          manager.cancelTimeout,
          manager.manageExternal,
          manager.setupTimeout),
        qh.makeOver(
          manager.clearEdgeOffset,
          manager.cancelTimeout)))

    manager.wrapH('performTickRequestHandler', manager.performTick)

    manager.getPrevModal = (actCurr) => {
      if (curr2prev.has(actCurr) && postListManager.contains(curr2prev.get(actCurr))) {
        return curr2prev.get(actCurr)
      }

      curr2prev.clear()

      const currPostIndex = postList.indexOf(actCurr)
      const prevDonePosts = postList.slice(0, currPostIndex).filter(postFlag.isDone).reverse()
      if (prevDonePosts.length === 0) {
        return null
      }

      const steps = 3 // goal number of steps to go through the entire gallery
      const perStep = Math.round(postListManager.getActualDonePostListSize() / steps)
      const maxOffset = Math.min(perStep, prevDonePosts.length - 1, 64)
      const minOffset = Math.round(maxOffset / 2)
      if (prevDonePosts.length <= 128 && !postListManager.isUnprocessedAboveListEmpty()) {
        const lastPrev = last(prevDonePosts)
        return curr2prev.set(actCurr, lastPrev)
      } else {
        const someIndex = random(minOffset, maxOffset)
        const somePrev = prevDonePosts[someIndex]
        return curr2prev.set(actCurr, somePrev)
      }
    }

    manager.getNextModal = (actCurr) => {
      if (curr2next.has(actCurr) && postListManager.contains(curr2next.get(actCurr))) {
        return curr2next.get(actCurr)
      }

      curr2next.clear()

      const nextPostIndex = postList.indexOf(actCurr) + 1
      const nextDonePosts = postList.slice(nextPostIndex, postList.length).filter(postFlag.isDone)
      if (nextDonePosts.length === 0) {
        return null
      }

      const steps = 3 // goal number of steps to go through the entire gallery
      const perStep = Math.round(postListManager.getActualDonePostListSize() / steps)
      const maxOffset = Math.min(perStep, nextDonePosts.length - 1, 64)
      const minOffset = Math.round(maxOffset / 2)
      if (nextDonePosts.length <= 64 && !postListManager.isUnprocessedBelowListEmpty()) {
        const lastNext = last(nextDonePosts)
        return curr2next.set(actCurr, lastNext)
      } else {
        const someIndex = random(minOffset, maxOffset)
        const someNext = nextDonePosts[someIndex]
        return curr2next.set(actCurr, someNext)
      }
    }

    return manager
  }
}
